import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { allArticles } from '@/mocks/articles';
import { SITE_URL } from '@/constants/site';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Header />
        <main className="pt-24 pb-14 md:pb-20">
          <div className="w-full px-4 md:px-6 text-center">
            <div className="max-w-lg mx-auto">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-accent-100">
                <i className="ri-emotion-sad-line text-2xl text-accent-600"></i>
              </div>
              <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
                Artigo não encontrado
              </h1>
              <p className="mt-3 text-sm text-foreground-600">
                O artigo que procura não está disponível. Explore os nossos outros conteúdos.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-6 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </span>
                Voltar ao início
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.metaTitle,
    description: article.metaDescription,
    image: article.image,
    author: { '@type': 'Organization', name: 'CSSBuySheet Portugal', url: SITE_URL },
    publisher: { '@type': 'Organization', name: 'CSSBuySheet Portugal', url: SITE_URL },
    datePublished: '2026-06-27',
    dateModified: '2026-06-27',
    inLanguage: 'pt-PT',
    url: SITE_URL + '/artigo/' + article.slug,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Header />
      <main className="pt-24 pb-14 md:pb-20">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-accent-600 transition-colors whitespace-nowrap">
                Início
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-[11px]"></i>
              </span>
              <span className="text-foreground-700 whitespace-nowrap truncate max-w-[200px]">{article.title}</span>
            </nav>

            <header className="mb-10">
              <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                Guia CSSBuy
              </span>
              <h1 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight tracking-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                {article.metaDescription}
              </p>
              <div className="flex items-center gap-4 mt-6 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-[11px]"></i>
                  </span>
                  Atualizado: 27 Junho 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-[11px]"></i>
                  </span>
                  8 min de leitura
                </span>
              </div>
            </header>

            <div className="mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
                loading="lazy"
              />
            </div>

            <div className="prose-custom">
              {article.content.map((block, index) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={index} className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mt-10 mb-4 leading-snug">
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-12 p-6 md:p-8 bg-accent-50 rounded-2xl border border-accent-200/50">
              <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">
                Pronto para começar?
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                Agora que conhece mais sobre o CSSBuy Spreadsheet, explore as categorias de produtos verificados e comece as suas compras com total segurança.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  CSSBuy Sheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm"></i>
                  </span>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 border border-foreground-300 text-foreground-700 px-6 py-3 rounded-full text-sm font-semibold hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-home-line text-sm"></i>
                  </span>
                  Voltar ao início
                </Link>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200">
              <Link
                to="/"
                className="inline-flex items-center gap-2 border border-foreground-300 text-foreground-700 px-6 py-3 rounded-full text-sm font-semibold hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </span>
                Voltar ao início
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}